// Module: PublishOverviewScreen
// Role: Overview screen for PUBLISH-kind checkpoints. Shows progress checklist
//       with SocialIcon badges, hashtag hint, and "don't delete" warning.
//       Unlike PickupOverviewScreen/UgcOverviewScreen, scans by kind='PUBLISH'
//       (flat checkpoints, no parent-child grouping in b-oz-split2).
// Uses: SocialIcon, Frame, MiniHeader, InstructionCard, WizardCtaBar, HintBar,
//       StepStrip, flowStepLabel, P/S/T tokens, flow-types
// Used by: FlowScreen.tsx (case 'PUBLISH' when multiple PUBLISH checkpoints)
// Design source: pantene-flow.jsx:1101 (PF_PublishOverview)

import { Frame } from '../atoms/Frame';
import { MiniHeader } from '../atoms/MiniHeader';
import { InstructionCard } from '../atoms/InstructionCard';
import { WizardCtaBar } from '../../../components/atoms/WizardCtaBar';
import { HintBar } from '../atoms/HintBar';
import { StepStrip } from '../StepStrip';
import { SocialIcon, platformFromSlug } from '../atoms/SocialIcon';
import { Warning } from '../atoms/Icons';
import { flowStepLabel } from '../_labels';
import { P, S, T } from '../../../styles/tokens';
import type { Checkpoint, CheckpointAttempt, SlotFlowRun } from '../../../api/flow-types';

interface TemplateProps {
  checkpoint: Checkpoint;
  attempt?: CheckpointAttempt;
  flowRun: SlotFlowRun;
  checkpoints: Checkpoint[];
  attempts: CheckpointAttempt[];
  onCta: () => void;
}

type PublishState = 'done' | 'active' | 'pending';

export function PublishOverviewScreen({
  checkpoint,
  checkpoints,
  attempts,
  onCta,
}: TemplateProps) {
  // Collect all PUBLISH-kind checkpoints sorted by position.
  const publishSteps = checkpoints
    .filter((c) => c.kind === 'PUBLISH')
    .sort((a, b) => a.position - b.position);

  const parentCount = checkpoints.filter((c) => c.parent_checkpoint_id === null).length;

  // Derive state for each publish step.
  let foundFirstNonDone = false;
  const states: PublishState[] = publishSteps.map((cp) => {
    const isDone = attempts.some(
      (a) => a.checkpoint_id === cp.id && a.verdict === 'accepted',
    );
    if (isDone) return 'done';
    if (!foundFirstNonDone) {
      foundFirstNonDone = true;
      return 'active';
    }
    return 'pending';
  });

  const doneCount = states.filter((s) => s === 'done').length;
  const totalCount = publishSteps.length;

  // Find the active step for CTA label.
  const activeIndex = states.indexOf('active');
  const activeStep = activeIndex >= 0 ? publishSteps[activeIndex] : null;

  // Metadata: hashtag from checkpoint or flow metadata.
  const meta = (checkpoint.metadata ?? {}) as Record<string, string>;
  const hashtag = meta.hashtag ?? null;

  if (publishSteps.length === 0) {
    return (
      <Frame>
        <MiniHeader title={flowStepLabel(checkpoint.kind)} />
        <div className="px-4 pt-3 pb-2">
          <StepStrip current={checkpoint.position + 1} total={parentCount} showCaption />
        </div>
        <div className="flex-1 overflow-y-auto px-4 pb-4 flex flex-col gap-4">
          <HintBar>Нет шагов публикации</HintBar>
        </div>
      </Frame>
    );
  }

  return (
    <Frame>
      <MiniHeader title={flowStepLabel(checkpoint.kind)} />
      <div className="px-4 pt-3 pb-2">
        <StepStrip current={checkpoint.position + 1} total={parentCount} showCaption />
      </div>
      <div className="flex-1 overflow-y-auto px-4 pb-4 flex flex-col gap-4">
        {/* Title + progress */}
        <div>
          <div style={{ font: T.title3, color: S.label, lineHeight: 1.2 }}>
            Опубликуй в {totalCount} местах
          </div>
          <div style={{ font: T.footnote, color: S.secondaryLabel, marginTop: 4 }}>
            {doneCount}/{totalCount} готово
          </div>
        </div>

        {/* Checklist cards */}
        <InstructionCard>
          <div className="flex flex-col gap-3">
            {publishSteps.map((cp, i) => {
              const state = states[i];
              const isDone = state === 'done';
              const isActive = state === 'active';
              const isPending = state === 'pending';
              const platform = platformFromSlug(cp.slug);

              return (
                <button
                  key={cp.id}
                  data-testid="publish-step-card"
                  data-state={state}
                  onClick={isActive ? onCta : undefined}
                  disabled={isPending}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: 14,
                    background: '#fff',
                    border: `${isActive ? 2 : 1}px solid ${isActive ? P.terracotta : P.line}`,
                    borderRadius: 14,
                    opacity: isPending ? 0.55 : 1,
                    cursor: isPending ? 'default' : 'pointer',
                    textAlign: 'left' as const,
                    transition: 'all 0.2s ease',
                  }}
                >
                  <SocialIcon platform={platform} done={isDone} active={isActive} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ font: T.headline, fontWeight: 600, color: S.label }}>
                      {cp.display_name ?? cp.slug}
                    </div>
                    <div style={{ font: T.caption, color: S.secondaryLabel, marginTop: 2 }}>
                      {cp.instructions_text ?? ''}
                    </div>
                  </div>
                  {isDone && (
                    <div style={{
                      font: T.tag, color: P.success, fontWeight: 700,
                      background: 'rgba(45,138,78,0.10)',
                      padding: '3px 8px', borderRadius: 999, flexShrink: 0,
                    }}>
                      принято
                    </div>
                  )}
                  {isActive && (
                    <div style={{ font: T.tag, color: P.terraDark, fontWeight: 700, flexShrink: 0 }}>
                      сейчас &rarr;
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </InstructionCard>

        {/* Hashtag hint (if present in metadata) */}
        {hashtag && (
          <div style={{
            background: '#fff', borderRadius: 14, padding: 12,
            border: `1px solid ${P.line}`,
          }}>
            <div style={{ font: T.caption, fontWeight: 700, color: S.label, marginBottom: 8 }}>
              Везде один хэштег:
            </div>
            <div style={{
              background: P.cream, padding: '8px 12px', borderRadius: 10,
              fontFamily: T.fontMono, fontSize: 14, color: P.terraDark,
              fontWeight: 700, display: 'inline-block',
            }}>
              {hashtag}
            </div>
          </div>
        )}

        {/* Warning -- don't delete posts */}
        <HintBar kind="warn" icon={<Warning size={14} />}>
          <b>Не удаляй посты</b> 16 дней. На 14-й день мы попросим скрин — это нужно для выплаты.
        </HintBar>
      </div>
      <WizardCtaBar
        label={activeStep?.display_name ?? 'Продолжить'}
        onClick={onCta}
      />
    </Frame>
  );
}
